import { useEffect, useRef } from "react";

import { SliceZone } from "@prismicio/react";
import _get from "lodash/get";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { FormDocumentDataSlicesSlice } from "prismicio-types";
import { useGetProduct } from "queries/product-queries";
import { useGetCustomisation } from "queries/product-queries";
import { components } from "slices/fields";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { useFindByKey } from "utils/schema-utils";

import styles from "./TabbedForm.module.scss";

export type TabbedFormTabs = {
  keys: string[];
  slices: FormDocumentDataSlicesSlice[];
  title: string;
}[];

type TabbedFormProps = {
  activeTabIndex: number;
  onTabChange: (index: number) => void;
  tabs: TabbedFormTabs;
};

/**
 * Sub-form used in Drawers, In-Page Edits and Creation Flow to support Avatars.
 */
export default function TabbedForm({
  activeTabIndex,
  onTabChange,
  tabs,
}: TabbedFormProps) {
  const { data: product } = useGetProduct();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const findByKey = useFindByKey();
  const { data: customisation } = useGetCustomisation();

  const progressRef = useRef(getProgress());

  useEffect(() => {
    if (activeTabIndex > progressRef.current) {
      progressRef.current = activeTabIndex;
    }
  }, [activeTabIndex]);

  function getProgress() {
    if (!tabs) {
      throw new Error("Missing tabs");
    }

    // Work out progress by finding the first tab with a missing field
    // (Assumes all fields are required)
    const latestCompletedTab = tabs.findIndex(({ keys }) =>
      keys.find((key) => {
        // The name of this function is the same of a proprietaty testing library
        // function so we we need to disable the eslint rule
        // eslint-disable-next-line testing-library/await-async-query
        return !_get(customisation, findByKey(key || "")?.urlKey || "");
      })
    );

    return latestCompletedTab > -1 ? latestCompletedTab : tabs.length - 1;
  }

  return (
    <>
      <div className={styles.tabs}>
        {tabs.map(({ keys, title }, i) => (
          <button
            key={keys.join(",")}
            disabled={i > progressRef.current}
            onClick={() => onTabChange(i)}
            type="button"
            className={[
              styles.tab,
              activeTabIndex === i ? styles.active : "",
            ].join(" ")}
          >
            {title}
          </button>
        ))}
      </div>

      <ErrorBoundary>
        <SliceZone
          components={components}
          slices={tabs[activeTabIndex].slices}
        />
      </ErrorBoundary>
    </>
  );
}
